import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useRequest, useToggle } from 'ahooks';
import Link from 'next/link';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import Breadcrumb from '@/components/breadcrumb';
import Footer from '@/business-components/footer';
import TabsAnchor from '@/business-components/tabs-anchor';
import Layout from '@/layouts';
import { ConBrandWebController } from '@/services/swagger/ConBrandWebController';
import type {
  BrandSocialDataType,
  ConBrandFansVo,
  ConBrandInfoVo,
  ConBrandParentCompanyVo,
  ConBrandWebV1FindResponse,
  ConBrandWebV1PotentialResponse,
  ConParentCompanyProductVo,
  FinanceForBrandDetailDO,
  IndexDataGrowType,
  IndexScoreType,
  UserNumTypeVo,
} from '@/services/swagger/data-contracts';
import Chart from '@/business-components/chart';
import ServerSideFetch from '@/utils/server-side-fetch';
import { ConBrandDetailsWebController } from '@/services/swagger/ConBrandDetailsWebController';
import RelativeBrandCard from '@/business-components/RelativeBrandCard';
import ReportCard from '@/business-components/ReportCard';
import MarkertingAbility from '@/business-components/MarketingAbility';
import SupplyChainManagementAbility from '@/business-components/SupplyChainManagementAbility';
import ProductCompetitiveness from '@/business-components/ProductCompetitiveness';
import RacetrackAndBrandPotential from '@/business-components/RacetrackAndBrandPotential';
import { getRelatedReportList } from '@/services/report';
import { Report } from '@/interface/report';
import useClick from '@/hooks/useClick';
import SupplyInfoContent from '@/business-components/SupplyInfoContent';
// import SubscribeBrandModalContent from '@/business-components/SubscribeBrandModalContent';
import { RootState } from '@/models';
// import BrandCompositeIndex from './components/brandCompositeIndex';
import UnlockConfirmModalContent from '@/business-components/UnlockConfirmModalContent';
import UnlockNumErrorModalContent from '@/business-components/UnlockNumErrorModalContent';
import BrandInfo from './components/brandInfo';
import Development from './components/development';
import Operate from './components/operate';
import InvestorScore from './components/investorScore';
import Organize from './components/organize';
import styles from './index.module.scss';

const controller = new ConBrandWebController();
const brandDetailController = new ConBrandDetailsWebController();

export type IndexPageServiderProps = {
  /** 品牌id */
  conBrandId: string;
  /** 品牌基本信息 */
  brandInfo?: ConBrandWebV1FindResponse;
  /** 增长指数 */
  growIndexList: IndexDataGrowType[];
  /** 八大指数 */
  indexScoreList: IndexScoreType[];
  /** 母公司信息 */
  conBrandParentCompanyVo?: ConBrandParentCompanyVo;
  /** 母公司品牌穿透图数据 */
  conParentCompanyProductVo?: ConParentCompanyProductVo;
  /** 融资情况 */
  financingInfo: ConBrandWebV1PotentialResponse;
  /** 融资历程表 */
  finanncingList: FinanceForBrandDetailDO[];
  /** 品牌产品数量 */
  productNum: number;
  /** 物流配送效率 */
  dsrValue: string;
  /** 官号总粉丝规模指数 */
  totalFans: number;
  /** 用户发展能力：官媒粉丝数 */
  fansList: UserNumTypeVo[];
  /** 用户发展能力：社交平台官号粉丝数量占比 */
  fansRateList: ConBrandFansVo[];
  /** 营销能力：品牌官号互动量：转发指数 */
  repostList: BrandSocialDataType[];
  /** 营销能力：品牌官号互动量：点赞指数 */
  likeList: BrandSocialDataType[];
  /** 营销能力：品牌官号互动量：评论指数 */
  commentList: BrandSocialDataType[];
  /** 同赛道相关品牌 */
  relativeBrandList: ConBrandInfoVo[];
  /** 品牌相关内容（报告/资讯） */
  reportList: Report[];
};
export const getServerSideProps: GetServerSideProps<IndexPageServiderProps, { id: string }> = async ({
  res,
  params,
}) => {
  res.setHeader('Cache-Control', 'public, s-maxage=15, stale-while-revalidate=59');
  const { id: conBrandId } = params;
  /** 品牌基本信息 */
  const { data: brandInfo } = await ServerSideFetch(controller.conBrandWebControllerV1FindUsingGet1, {
    conBrandId,
  });
  /** 增长力综合指数 */
  const { data: indexData } = await ServerSideFetch(controller.conBrandWebControllerV1IndexDataUsingGet1, {
    conBrandId,
  });
  /** 社交平台官号粉丝规模/社交平台官号粉丝数量占比 */
  const { data: userScale } = await ServerSideFetch(
    brandDetailController.conBrandDetailsWebControllerV1UserScaleNiceUsingGet1,
    {
      conBrandId,
    }
  );
  /** 组织能力：母公司信息、母公司品牌穿透图 */
  const { data: organization } = await ServerSideFetch(
    brandDetailController.conBrandDetailsWebControllerV1BrandDetailOrganUsingGet1,
    {
      conBrandId,
    }
  );
  /** 行业及品牌发展潜力 */
  const { data: potential } = await ServerSideFetch(controller.conBrandWebControllerV1TrackBrandPotentialUsingGet1, {
    conBrandId,
  });
  /** 产品竞争力：产品数量 */
  const { data: productInfo } = await ServerSideFetch(
    brandDetailController.conBrandDetailsWebControllerV1BrandProductInfoUsingGet1,
    {
      conBrandId,
    }
  );
  /** 供应链管理能力：物流配送效率 */
  const { data: supplyChain } = await ServerSideFetch(controller.conBrandWebControllerV1SupplyChainUsingGet1, {
    conBrandId,
  });
  /** 营销能力：品牌官方账号内容互动量 */
  const { data: marketing } = await ServerSideFetch(controller.conBrandWebControllerV1MarketingCapacityUsingGet1, {
    conBrandId,
  });
  /** 同赛道其他品牌 */
  const { data: relative } = await ServerSideFetch(
    brandDetailController.conBrandDetailsWebControllerV1TrackOtherBrandUsingGet1,
    {
      conBrandId,
    }
  );
  /** 品牌相关内容（报告/资讯） */
  const { data: report } = await getRelatedReportList({
    keyword: brandInfo?.chName || brandInfo?.ehName,
    pageSize: 10,
  });
  return {
    props: {
      /** 品牌id */
      conBrandId,
      /** 品牌基本信息 */
      brandInfo: brandInfo || {},
      /** 增长力综合指数：增长指数 */
      growIndexList:
        indexData?.growIndexList.map(item => {
          let dataTypeName;
          if (item.dataType === 'grow_new_brand') {
            dataTypeName = '新消费整体综合指数';
          } else if (item.dataType === 'grow_industry') {
            dataTypeName = `${indexData.industryTrackName}行业综合指数`;
          } else if (item.dataType === 'grow_track') {
            dataTypeName = `${indexData.oneLevelTrackName}赛道综合指数`;
          } else if (item.dataType === 'grow_composite') {
            dataTypeName = `${indexData.brandName}综合指数`;
          }
          return {
            ...item,
            dataTypeName,
          };
        }) || [],
      /** 增长力综合指数：八大指数 */
      indexScoreList: indexData?.indexScoreList || [],
      /** 组织能力：母公司信息 */
      conBrandParentCompanyVo: organization?.conBrandParentCompanyVo || {},
      /** 组织能力：母公司品牌穿透图 */
      conParentCompanyProductVo: organization?.conParentCompanyProductVo || {},
      /** 行业及品牌发展潜力：融资情况 */
      financingInfo: potential || {},
      /** 行业及品牌发展潜力：融资历程表 */
      finanncingList: potential?.financeList || [],
      /** 产品竞争力：品牌产品数量 */
      productNum: productInfo?.productNum || 0,
      /** 供应链管理能力：物流配送效率 */
      dsrValue: supplyChain?.dsrValue || '',
      /** 用户发展能力：官号总粉丝规模指数 */
      totalFans: userScale?.totalFans || 0,
      /** 用户发展能力：官媒粉丝数 */
      fansList: userScale?.fansList || [],
      /** 用户发展能力：社交平台官号粉丝数量占比 */
      fansRateList: userScale?.fansRateList || [],
      /** 营销能力：品牌官号互动量：转发指数 */
      repostList: marketing?.forwardList || [],
      /** 营销能力：品牌官号互动量：点赞指数 */
      likeList: marketing?.likeList || [],
      /** 营销能力：品牌官号互动量：评论指数 */
      commentList: marketing?.commentList || [],
      /** 同赛道相关品牌 */
      relativeBrandList: relative?.brandList || [],
      /** 品牌相关内容（报告/资讯） */
      reportList: report?.list || [],
    },
  };
};

function BrandPage({
  conBrandId,
  brandInfo,
  // growIndexList,
  // indexScoreList,
  conBrandParentCompanyVo,
  conParentCompanyProductVo,
  financingInfo,
  finanncingList,
  productNum,
  dsrValue,
  fansList,
  fansRateList,
  totalFans,
  repostList,
  likeList,
  commentList,
  relativeBrandList,
  reportList,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { token } = useSelector((state: RootState) => state.user);
  const { canUnLockBalanceNum } = useSelector((state: RootState) => state.user.memberInfo);

  const dispatch = useDispatch();
  const [isHidden, { toggle }] = useToggle(false);
  const { onClick: onContrastBtnClick } = useClick();
  const { onClick: onVerificationBtnClick } = useClick();
  const [selectedKeys, setSelectedKeys] = useState('2');
  const { onClick: onSupplyInfoBtnClick } = useClick({
    afterLogin: () => {
      dispatch({
        type: 'modal/open',
        payload: {
          content: <SupplyInfoContent conBrandId={conBrandId} defaultActiveKey="coreTeam" />,
          title: '补充信息',
        },
      });
    },
  });

  const {
    data: authData = {},
    mutate,
    refresh,
  } = useRequest(
    async () => {
      const res = await controller.conBrandWebControllerV1UserBrandAuthUsingGet1({ conBrandId });
      const {
        /** 是否是会员 */
        hasMember,
        /** 品牌详情页-查看投资人评分(是否是投资人) */
        hasInvestorUser,
        /** 是否留资 */
        hasLiuZi,
        memberType,
        memberTypeName,
        alreadySubNum,
        canSubBalanceNum,
        totalCanSubNum,
        alreadyUnLockNum,
        canUnLockBalanceNum,
        totalCanUnLockNum,
        email,
      } = res.data.data;
      dispatch({
        type: 'user/setMemberInfo',
        payload: {
          hasMember,
          hasInvestorUser,
          hasLiuZi,
          memberType,
          memberTypeName,
          alreadySubNum,
          totalCanSubNum,
          canSubBalanceNum,
          alreadyUnLockNum,
          canUnLockBalanceNum,
          totalCanUnLockNum,
          email,
        },
      });
      return res.data.data;
    },
    {
      refreshDeps: [token],
      // onSuccess(data) {
      //   if (token && !data.hasLiuZi) {
      //     dispatch({
      //       type: 'modal/open',
      //       payload: {
      //         content: <SubscribeBrandModalContent onSubmitEmailSuccess={() => refresh()} />,
      //       },
      //     });
      //   }
      // },
    }
  );

  const onLockedCardStateChange = useCallback(() => {
    refresh();
  }, [refresh]);
  const onSubmitEmailSuccess = useCallback(() => {
    refresh();
  }, [refresh]);
  const { onClick: onInvestorBtnClick } = useClick({
    afterLogin: () => {
      if (authData.brandLockState === 'locked') {
        canUnLockBalanceNum
          ? dispatch({
              type: 'modal/open',
              payload: {
                content: <UnlockConfirmModalContent conBrandId={conBrandId} onUnlocked={onLockedCardStateChange} />,
              },
            })
          : dispatch({
              type: 'modal/open',
              payload: {
                content: <UnlockNumErrorModalContent />,
              },
            });
      } else {
        dispatch({
          type: 'modal/open',
          payload: {
            content: (
              <InvestorScore conBrandId={conBrandId} showCompanyScore showGroupScore showSupplyChainScore showRate />
            ),
            title: '投资人评分',
          },
        });
      }
    },
  });

  const tabArr = useMemo(
    () => [
      // {
      //   id: '1',
      //   name: '增长力综合指数',
      //   content: <BrandCompositeIndex growIndexList={growIndexList} indexScoreList={indexScoreList} />,
      // },
      {
        id: '2',
        name: '组织能力',
        content: (
          <Organize
            conBrandId={conBrandId}
            isBrandVendor={authData.hasThisBrandMember}
            brandLockState={authData.brandLockState}
            conBrandParentCompanyVo={conBrandParentCompanyVo}
            conParentCompanyProductVo={conParentCompanyProductVo}
            onLockedCardStateChange={onLockedCardStateChange}
          />
        ),
      },
      {
        id: '3',
        name: '经营能力',
        content: (
          <Operate
            conBrandId={conBrandId}
            brandLockState={authData.brandLockState}
            onLockedCardStateChange={onLockedCardStateChange}
          />
        ),
      },
      {
        id: '4',
        name: '行业及品牌发展潜力',
        content: (
          <RacetrackAndBrandPotential
            conBrandId={conBrandId}
            brandLockState={authData.brandLockState}
            financingInfo={financingInfo}
            finanncingList={finanncingList}
            onLockedCardStateChange={onLockedCardStateChange}
          />
        ),
      },
      {
        id: '5',
        name: '产品竞争能力',
        content: (
          <ProductCompetitiveness
            conBrandId={conBrandId}
            brandLockState={authData.brandLockState}
            productNum={productNum}
            onLockedCardStateChange={onLockedCardStateChange}
          />
        ),
      },
      {
        id: '6',
        name: '供应链管理能力',
        content: (
          <SupplyChainManagementAbility
            conBrandId={conBrandId}
            isBrandVendor={authData.hasThisBrandMember}
            brandLockState={authData.brandLockState}
            dsrValue={dsrValue}
            onLockedCardStateChange={onLockedCardStateChange}
          />
        ),
      },
      {
        id: '7',
        name: '用户发展能力',
        content: (
          <Development
            conBrandId={conBrandId}
            brandLockState={authData.brandLockState}
            totalFans={totalFans}
            fansList={fansList}
            fansRateList={fansRateList}
            onLockedCardStateChange={onLockedCardStateChange}
          />
        ),
      },
      {
        id: '8',
        name: '营销能力',
        content: (
          <MarkertingAbility
            conBrandId={conBrandId}
            brandLockState={authData.brandLockState}
            repostList={repostList}
            likeList={likeList}
            commentList={commentList}
            onLockedCardStateChange={onLockedCardStateChange}
          />
        ),
      },
    ],
    [
      authData.brandLockState,
      authData.hasThisBrandMember,
      commentList,
      conBrandId,
      conBrandParentCompanyVo,
      conParentCompanyProductVo,
      dsrValue,
      fansList,
      fansRateList,
      financingInfo,
      finanncingList,
      likeList,
      onLockedCardStateChange,
      productNum,
      repostList,
      totalFans,
    ]
  );
  const onSubscriptionStateChange = useCallback(
    value => {
      mutate(oldValue => {
        return { ...oldValue, hasSubed: value };
      });
    },
    [mutate]
  );
  const onCollectionStateChange = useCallback(
    value => {
      mutate(oldValue => {
        return { ...oldValue, hasCollect: value };
      });
    },
    [mutate]
  );
  const handleChange = id => {
    setSelectedKeys(id);
  };
  useEffect(() => {
    addEventListener(
      'scroll',
      debounce(() => {
        if (!location.hash) {
          setSelectedKeys('1');
          // setOpenKeys(['1']);
        }
      }, 150)
    );
    addEventListener('hashchange', () => {
      setSelectedKeys(location.hash.replace('#tabs-', ''));
    });
  }, []);
  return (
    <>
      <Layout
        title={(brandInfo?.chName || brandInfo?.ehName) + ' | 品牌数集'}
        description="第一财经商业数据中心（CBNData）隶属于上海文化广播影视集团（SMG），是是国内领先的消费研究机构及数字化增长服务商。业务场景已覆盖传统零售与电商、互联网消费、内容社交、医疗教育、金融理财等多领域，面向品牌企业全面输出数据应用、内容运营、整合营销、销售转化等数字化增长服务。"
        keyword="cbn，CBNData，数据报告，第一财经，消费行业研究报告，营销案例，整合营销，数字营销，新零售，消费新趋势，数智化经济，数字化经济"
        isShowNavBar
        isFrapMain
        FooterComponent={Footer}
      >
        <Breadcrumb
          list={[
            { href: '/', title: '首页' },
            { href: '/brand', title: '品牌库' },
            { title: brandInfo?.chName || brandInfo?.ehName },
          ]}
          className={styles.bradcrumb}
        />
        <div className={classnames('flex-row-nowrap', styles.content)}>
          <div className={styles.leftContent}>
            <BrandInfo
              brandId={conBrandId}
              isBrandVendor={authData.hasThisBrandMember}
              subscribed={authData.hasSubed}
              collected={authData.hasCollect}
              {...brandInfo}
              onSubscriptionStateChange={onSubscriptionStateChange}
              onCollectionStateChange={onCollectionStateChange}
              onSubmitEmailSuccess={onSubmitEmailSuccess}
            />
            {/* <TabsAnchor list={tabArr} /> */}
            <TabsAnchor selectedKeys={selectedKeys} onSelectChanged={handleChange} tabData={tabArr} />
          </div>
          <div className={styles.rightContent}>
            <Chart title={null} header={false} panelClassName={styles.brandActionChart}>
              <Link href={`/brand-contrast/${conBrandId}`}>
                <a
                  className={classnames(styles.brandActionBtn, styles.contrastBtn)}
                  target="_blank"
                  onClick={onContrastBtnClick}
                >
                  <img
                    className={styles.panelIcon}
                    src="//assets-oss.cbndata.com/cbndata-refactor-fe/FtpGfcBIETi5aWMrc-y-Kbg4hd9c.png"
                    alt="品牌对比"
                  />
                  <span>品牌对比</span>
                  <svg
                    className={styles.arrowIcon}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.72656 11.06L8.7799 8L5.72656 4.94L6.66656 4L10.6666 8L6.66656 12L5.72656 11.06Z"
                      fill="white"
                    />
                  </svg>
                </a>
              </Link>
              {authData.hasThisBrandMember ? (
                <a className={classnames(styles.brandActionBtn, styles.verificationBtn)} onClick={onSupplyInfoBtnClick}>
                  <img
                    className={styles.panelIcon}
                    src="//assets-oss.cbndata.com/cbndata-refactor-fe/FshUp47M-BT52HiB99Q7r1Nn0JCw.png"
                    alt="补充信息"
                  />
                  <span>补充信息</span>
                  <svg
                    className={styles.arrowIcon}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.72656 11.06L8.7799 8L5.72656 4.94L6.66656 4L10.6666 8L6.66656 12L5.72656 11.06Z"
                      fill="white"
                    />
                  </svg>
                </a>
              ) : (
                <Link href={`/brand/${conBrandId}/brandCertification`}>
                  <a
                    target="_blank"
                    className={classnames(styles.brandActionBtn, styles.verificationBtn)}
                    onClick={onVerificationBtnClick}
                  >
                    <img
                      className={styles.panelIcon}
                      src="//assets-oss.cbndata.com/cbndata-refactor-fe/FklA0GtQkOnojn6wjN4EpGfuujwd.png"
                      alt="品牌方认证"
                    />
                    <span>品牌方认证</span>
                    <svg
                      className={styles.arrowIcon}
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.72656 11.06L8.7799 8L5.72656 4.94L6.66656 4L10.6666 8L6.66656 12L5.72656 11.06Z"
                        fill="white"
                      />
                    </svg>
                  </a>
                </Link>
              )}

              <a className={classnames(styles.brandActionBtn, styles.investorBtn)} onClick={onInvestorBtnClick}>
                <img
                  className={styles.panelIcon}
                  src="//assets-oss.cbndata.com/cbndata-refactor-fe/FnODfVxEQIs34UC0ujKer0o1lL_Y.png"
                  alt="查看投资人评分"
                />
                <span>查看投资人评分</span>
                <svg
                  className={styles.arrowIcon}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.72656 11.06L8.7799 8L5.72656 4.94L6.66656 4L10.6666 8L6.66656 12L5.72656 11.06Z"
                    fill="white"
                  />
                </svg>
              </a>
            </Chart>
            {relativeBrandList.length >= 3 && (
              <Chart title="同行业热门品牌推荐" panelClassName={styles.relativeBrandListChart}>
                <div className={isHidden ? styles['relativeBrandList--hidden'] : styles.relativeBrandList}>
                  {relativeBrandList.map(item => {
                    return <RelativeBrandCard className={styles.relativeBrandCard} key={item.conBrandId} {...item} />;
                  })}
                </div>
                <div className={styles.toggle}>
                  <span className={styles.toggleText} onClick={toggle}>
                    {isHidden ? '展开' : '收起'}全部
                  </span>
                  <svg className="icon" aria-hidden="true">
                    <use xlinkHref={isHidden ? '#icon-keyboard_arrow_down' : '#icon-keyboard_arrow_up'} />
                  </svg>
                </div>
              </Chart>
            )}
            {!!reportList.length && (
              <Chart title="品牌相关内容" panelClassName={styles.reportListChart}>
                <div>
                  {reportList
                    .map(item => {
                      return {
                        id: item.id,
                        reportId: item.oldId,
                        cover: item.cover,
                        title: item.title,
                        tags: item.tags,
                        clickTimes: item.clickTimes,
                        createTime: item.publishedAt,
                        updateTime: item.updatedAt,
                        type: item.type,
                        hottest: item.hottest,
                        latest: item.latest,
                        preSell: item.preSell,
                        referenced: item.referenced,
                      };
                    })
                    .map(item => {
                      return <ReportCard className={styles.reportCard} key={item.id} {...item} />;
                    })}
                </div>
              </Chart>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
}

export default BrandPage;
